'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function ResistorCalculator() {
  const t = useTranslations("tools.resistorCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Color Code Calculator State
  const [bandCount, setBandCount] = useState(4);
  const [band1, setBand1] = useState('brown');
  const [band2, setBand2] = useState('black');
  const [band3, setBand3] = useState('black');
  const [multiplier, setMultiplier] = useState('red');
  const [tolerance, setTolerance] = useState('gold');
  const [tempCoef, setTempCoef] = useState('brown');
  const [colorCodeResults, setColorCodeResults] = useState<any>(null);

  // Series Calculator State
  const [seriesResistors, setSeriesResistors] = useState<number[]>([0, 0]);
  const [seriesResult, setSeriesResult] = useState<number | null>(null);

  // Parallel Calculator State
  const [parallelResistors, setParallelResistors] = useState<number[]>([0, 0]);
  const [parallelResult, setParallelResult] = useState<number | null>(null);

  // Active Tab
  const [activeTab, setActiveTab] = useState('colorCode');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const colorValues: { [key: string]: number } = {
    'black': 0, 'brown': 1, 'red': 2, 'orange': 3, 'yellow': 4,
    'green': 5, 'blue': 6, 'violet': 7, 'grey': 8, 'white': 9
  };

  const multiplierValues: { [key: string]: number } = {
    'black': 1, 'brown': 10, 'red': 100, 'orange': 1000, 'yellow': 10000,
    'green': 100000, 'blue': 1000000, 'violet': 10000000, 'grey': 100000000,
    'white': 1000000000, 'gold': 0.1, 'silver': 0.01
  };

  const toleranceValues: { [key: string]: number } = {
    'brown': 1, 'red': 2, 'green': 0.5, 'blue': 0.25, 'violet': 0.1,
    'grey': 0.05, 'gold': 5, 'silver': 10
  };

  const tempCoefValues: { [key: string]: number } = {
    'brown': 100, 'red': 50, 'orange': 15, 'yellow': 25, 'blue': 10, 'violet': 5
  };

  const colorHex: { [key: string]: string } = {
    'black': '#000', 'brown': '#964b00', 'red': '#ff0000', 'orange': '#ffa500',
    'yellow': '#ffff00', 'green': '#9acd32', 'blue': '#6495ed', 'violet': '#9400d3',
    'grey': '#a0a0a0', 'white': '#fff', 'gold': '#cfb53b', 'silver': '#c0c0c0'
  };

  const digitColors = ['brown', 'red', 'orange', 'yellow', 'green', 'blue', 'violet', 'grey', 'white'];
  const allDigitColors = ['black', ...digitColors];
  const multiplierColors = ['black', 'brown', 'red', 'orange', 'yellow', 'green', 'blue', 'violet', 'grey', 'white', 'gold', 'silver'];
  const toleranceColors = ['brown', 'red', 'green', 'blue', 'violet', 'grey', 'gold', 'silver'];
  const tempCoefColors = ['brown', 'red', 'orange', 'yellow', 'blue', 'violet'];

  const formatResistance = (value: number): string => {
    if (value >= 1000000) return (value / 1000000).toFixed(2) + ' MΩ';
    if (value >= 1000) return (value / 1000).toFixed(2) + ' kΩ';
    return value.toFixed(2) + ' Ω';
  };

  const handleColorCodeCalculate = (e: React.FormEvent) => {
    e.preventDefault();

    let resistance: number;
    if (bandCount >= 5) {
      resistance = (colorValues[band1] * 100 + colorValues[band2] * 10 + colorValues[band3]) * multiplierValues[multiplier];
    } else {
      resistance = (colorValues[band1] * 10 + colorValues[band2]) * multiplierValues[multiplier];
    }

    const tolerancePercent = toleranceValues[tolerance];
    const minValue = resistance * (1 - tolerancePercent / 100);
    const maxValue = resistance * (1 + tolerancePercent / 100);

    setColorCodeResults({
      resistance: formatResistance(resistance),
      tolerance: `±${tolerancePercent}%`,
      minValue: formatResistance(minValue),
      maxValue: formatResistance(maxValue),
      tempCoef: bandCount === 6 ? `${tempCoefValues[tempCoef]} ppm/K` : null
    });
  };

  const handleSeriesCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const total = seriesResistors.reduce((sum, r) => sum + (r || 0), 0);
    setSeriesResult(total);
  };

  const handleParallelCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    let reciprocalSum = 0;
    parallelResistors.forEach(r => {
      if (r > 0) reciprocalSum += 1 / r;
    });
    const total = reciprocalSum > 0 ? 1 / reciprocalSum : 0;
    setParallelResult(total);
  };

  const addSeriesResistor = () => {
    setSeriesResistors([...seriesResistors, 0]);
  };

  const removeSeriesResistor = (index: number) => {
    if (seriesResistors.length > 2) {
      setSeriesResistors(seriesResistors.filter((_, i) => i !== index));
    }
  };

  const addParallelResistor = () => {
    setParallelResistors([...parallelResistors, 0]);
  };

  const removeParallelResistor = (index: number) => {
    if (parallelResistors.length > 2) {
      setParallelResistors(parallelResistors.filter((_, i) => i !== index));
    }
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '1.5rem' },
      { padding: '2rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
        borderRadius: '1rem',
        marginBottom: '2rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)'
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
      { padding: '3rem 2rem', borderRadius: '1.5rem' }
    ),
    toolHeader: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '1.5rem' },
      {},
      { marginBottom: '2rem' },
      { marginBottom: '2.5rem' }
    ),
    h2: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    tabs: getResponsiveStyle(
      { display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap' as const, justifyContent: 'center' as const },
      {},
      { gap: '1rem', marginBottom: '2rem' },
      {}
    ),
    tabBtn: getResponsiveStyle(
      {
        border: '2px solid #cbd5e1',
        background: 'white',
        borderRadius: '0.75rem',
        cursor: 'pointer',
        fontWeight: 600,
        color: '#64748b',
        transition: 'all 0.3s ease'
      },
      { padding: '0.625rem 1rem', fontSize: '0.875rem' },
      { padding: '0.75rem 1.5rem', fontSize: '1rem' },
      {}
    ),
    tabBtnActive: {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      borderColor: 'transparent'
    },
    form: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
        marginBottom: '1.5rem'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      {}
    ),
    formGroup: { marginBottom: '1.5rem' },
    label: {
      display: 'block',
      fontWeight: 600,
      color: '#334155',
      marginBottom: '0.5rem',
      fontSize: '0.95rem'
    },
    select: getResponsiveStyle(
      {
        width: '100%',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        fontSize: '1rem',
        transition: 'border-color 0.3s ease'
      },
      { padding: '0.75rem' },
      {},
      {}
    ),
    input: getResponsiveStyle(
      {
        width: '100%',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        fontSize: '1rem',
        transition: 'border-color 0.3s ease'
      },
      { padding: '0.75rem' },
      {},
      {}
    ),
    bandSelector: { marginBottom: '1.5rem' },
    bandLabel: { fontWeight: 600, color: '#334155', fontSize: '0.95rem', marginBottom: '0.75rem' },
    colorOptions: getResponsiveStyle(
      { display: 'grid', gap: '0.5rem' },
      { gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))' },
      { gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))' },
      {}
    ),
    colorOption: { position: 'relative' as const },
    colorRadio: { position: 'absolute' as const, opacity: 0 },
    colorLabel: getResponsiveStyle(
      {
        display: 'block',
        padding: '0.75rem',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        textAlign: 'center' as const,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        fontWeight: 500
      },
      { fontSize: '0.85rem' },
      { fontSize: '0.9rem' },
      {}
    ),
    colorLabelChecked: {
      borderColor: '#2563eb',
      boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.1)',
      transform: 'scale(1.05)'
    },
    resistorVisual: getResponsiveStyle(
      {
        background: '#f8fafc',
        padding: '2rem',
        borderRadius: '0.75rem',
        margin: '1.5rem 0',
        textAlign: 'center' as const,
        overflow: 'auto' as const
      },
      {},
      {},
      {}
    ),
    resistorBody: getResponsiveStyle(
      {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.25rem',
        background: '#e0d5c7',
        padding: '1.5rem 1rem',
        borderRadius: '2rem',
        position: 'relative' as const
      },
      { transform: 'scale(0.8)' },
      { transform: 'scale(0.9)' },
      { transform: 'scale(1)' }
    ),
    resistorLead: { width: '60px', height: '3px', background: '#94a3b8' },
    resistorBand: { width: '12px', height: '60px', borderRadius: '2px' },
    resistorSpacer: { width: '20px' },
    calculateBtn: getResponsiveStyle(
      {
        width: '100%',
        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '0.75rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease'
      },
      { padding: '0.875rem 1.5rem', fontSize: '1rem' },
      { padding: '1rem 2rem', fontSize: '1.1rem' },
      {}
    ),
    results: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)'
      },
      { padding: '1.5rem' },
      { padding: '2rem' },
      {}
    ),
    resultsTitle: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '1.5rem', textAlign: 'center' as const },
      { fontSize: '1.25rem' },
      { fontSize: '1.5rem' },
      {}
    ),
    resultGrid: { display: 'grid', gap: '1rem' },
    resultItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '1rem',
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '0.75rem',
      borderLeft: '4px solid #2563eb'
    },
    resultHighlight: {
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      borderLeft: '4px solid #f59e0b',
      padding: '1.5rem'
    },
    resultLabel: { fontWeight: 600, color: '#334155' },
    resultValue: getResponsiveStyle(
      { fontWeight: 700, color: '#2563eb' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' },
      {}
    ),
    resultValueHighlight: getResponsiveStyle(
      { fontWeight: 700, color: '#f59e0b' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      {}
    ),
    resistorInputGroup: getResponsiveStyle(
      { display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem' },
      {},
      {},
      {}
    ),
    addBtn: getResponsiveStyle(
      {
        color: '#2563eb',
        border: '2px solid #2563eb',
        background: 'white',
        borderRadius: '0.5rem',
        cursor: 'pointer',
        fontWeight: 600,
        transition: 'all 0.3s ease',
        marginTop: '1rem'
      },
      { padding: '0.625rem 1.25rem' },
      { padding: '0.75rem 1.5rem' },
      {}
    ),
    removeBtn: {
      color: '#ef4444',
      border: '2px solid #ef4444',
      background: 'white',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      fontWeight: 600,
      transition: 'all 0.3s ease',
      padding: '0.5rem 1rem',
      minWidth: '44px'
    },
    glassCard: getResponsiveStyle(
      {
        background: colors.glassBg,
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: `1px solid ${colors.glassBorder}`,
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
        marginBottom: '2rem'
      },
      { padding: '1.5rem' },
      { padding: '2rem', borderRadius: '1.25rem' },
      { padding: '2.5rem', borderRadius: '1.5rem' }
    ),
    paragraph: getResponsiveStyle(
      { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem', lineHeight: 1.7 },
      { fontSize: '1.25rem', lineHeight: 1.8 }
    ),
    list: getResponsiveStyle(
      { marginLeft: '1.5rem', color: colors.textSecondary },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    listItem: { marginBottom: '0.75rem', lineHeight: 1.6 },
    faqItem: { marginBottom: '1.5rem' },
    faqQuestion: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' },
      {}
    ),
    faqAnswer: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      {}
    ),
    refLink: {
      color: colors.primary,
      textDecoration: 'none',
      borderBottom: `1px solid ${colors.primary}`,
      transition: 'color 0.3s ease'
    }
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.h1}>{t("title")}</h1>
        <p style={styles.summaryText}>{t("subtitle")}</p>
      </header>

      <section style={styles.calculatorTool}>
        <div style={styles.toolHeader}>
          <h2 style={styles.h2}>{t("toolTitle")}</h2>
        </div>

        <div style={styles.tabs}>
          <button
            style={{...styles.tabBtn, ...(activeTab === 'colorCode' ? styles.tabBtnActive : {})}}
            onClick={() => setActiveTab('colorCode')}
          >
            {t("colorCodeTab")}
          </button>
          <button
            style={{...styles.tabBtn, ...(activeTab === 'series' ? styles.tabBtnActive : {})}}
            onClick={() => setActiveTab('series')}
          >
            {t("seriesTab")}
          </button>
          <button
            style={{...styles.tabBtn, ...(activeTab === 'parallel' ? styles.tabBtnActive : {})}}
            onClick={() => setActiveTab('parallel')}
          >
            {t("parallelTab")}
          </button>
        </div>

        {activeTab === 'colorCode' && (
          <div>
            <form style={styles.form} onSubmit={handleColorCodeCalculate}>
              <div style={styles.formGroup}>
                <label style={styles.label}>{t("bandCountLabel")}</label>
                <select
                  style={styles.select}
                  value={bandCount}
                  onChange={(e) => setBandCount(parseInt(e.target.value))}
                >
                  <option value="4">{t("band4")}</option>
                  <option value="5">{t("band5")}</option>
                  <option value="6">{t("band6")}</option>
                </select>
              </div>

              <div style={styles.bandSelector}>
                <div style={styles.bandLabel}>{t("band1Label")}</div>
                <div style={styles.colorOptions}>
                  {digitColors.map(color => (
                    <div key={color} style={styles.colorOption}>
                      <input
                        type="radio"
                        style={styles.colorRadio}
                        name="band1"
                        value={color}
                        checked={band1 === color}
                        onChange={(e) => setBand1(e.target.value)}
                        id={`band1-${color}`}
                      />
                      <label
                        htmlFor={`band1-${color}`}
                        style={{
                          ...styles.colorLabel,
                          background: colorHex[color],
                          color: ['black', 'brown', 'red', 'blue', 'violet'].includes(color) ? '#fff' : '#000',
                          ...(band1 === color ? styles.colorLabelChecked : {})
                        }}
                      >
                        {t(`color_${color}`)}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div style={styles.bandSelector}>
                <div style={styles.bandLabel}>{t("band2Label")}</div>
                <div style={styles.colorOptions}>
                  {allDigitColors.map(color => (
                    <div key={color} style={styles.colorOption}>
                      <input
                        type="radio"
                        style={styles.colorRadio}
                        name="band2"
                        value={color}
                        checked={band2 === color}
                        onChange={(e) => setBand2(e.target.value)}
                        id={`band2-${color}`}
                      />
                      <label
                        htmlFor={`band2-${color}`}
                        style={{
                          ...styles.colorLabel,
                          background: colorHex[color],
                          color: ['black', 'brown', 'red', 'blue', 'violet'].includes(color) ? '#fff' : '#000',
                          border: color === 'white' ? '2px solid #ccc' : '2px solid #e2e8f0',
                          ...(band2 === color ? styles.colorLabelChecked : {})
                        }}
                      >
                        {t(`color_${color}`)}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {bandCount >= 5 && (
                <div style={styles.bandSelector}>
                  <div style={styles.bandLabel}>{t("band3Label")}</div>
                  <div style={styles.colorOptions}>
                    {allDigitColors.map(color => (
                      <div key={color} style={styles.colorOption}>
                        <input
                          type="radio"
                          style={styles.colorRadio}
                          name="band3"
                          value={color}
                          checked={band3 === color}
                          onChange={(e) => setBand3(e.target.value)}
                          id={`band3-${color}`}
                        />
                        <label
                          htmlFor={`band3-${color}`}
                          style={{
                            ...styles.colorLabel,
                            background: colorHex[color],
                            color: ['black', 'brown', 'red', 'blue', 'violet'].includes(color) ? '#fff' : '#000',
                            border: color === 'white' ? '2px solid #ccc' : '2px solid #e2e8f0',
                            ...(band3 === color ? styles.colorLabelChecked : {})
                          }}
                        >
                          {t(`color_${color}`)}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={styles.bandSelector}>
                <div style={styles.bandLabel}>{t("multiplierLabel")}</div>
                <div style={styles.colorOptions}>
                  {multiplierColors.map(color => (
                    <div key={color} style={styles.colorOption}>
                      <input
                        type="radio"
                        style={styles.colorRadio}
                        name="multiplier"
                        value={color}
                        checked={multiplier === color}
                        onChange={(e) => setMultiplier(e.target.value)}
                        id={`multiplier-${color}`}
                      />
                      <label
                        htmlFor={`multiplier-${color}`}
                        style={{
                          ...styles.colorLabel,
                          background: colorHex[color],
                          color: ['black', 'brown', 'red', 'blue', 'violet'].includes(color) ? '#fff' : '#000',
                          border: color === 'white' ? '2px solid #ccc' : '2px solid #e2e8f0',
                          ...(multiplier === color ? styles.colorLabelChecked : {})
                        }}
                      >
                        {t(`color_${color}`)}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div style={styles.bandSelector}>
                <div style={styles.bandLabel}>{t("toleranceLabel")}</div>
                <div style={styles.colorOptions}>
                  {toleranceColors.map(color => (
                    <div key={color} style={styles.colorOption}>
                      <input
                        type="radio"
                        style={styles.colorRadio}
                        name="tolerance"
                        value={color}
                        checked={tolerance === color}
                        onChange={(e) => setTolerance(e.target.value)}
                        id={`tolerance-${color}`}
                      />
                      <label
                        htmlFor={`tolerance-${color}`}
                        style={{
                          ...styles.colorLabel,
                          background: colorHex[color],
                          color: ['brown', 'red', 'blue', 'violet'].includes(color) ? '#fff' : '#000',
                          ...(tolerance === color ? styles.colorLabelChecked : {})
                        }}
                      >
                        {t(`color_${color}`)}
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {bandCount === 6 && (
                <div style={styles.bandSelector}>
                  <div style={styles.bandLabel}>{t("tempCoefLabel")}</div>
                  <div style={styles.colorOptions}>
                    {tempCoefColors.map(color => (
                      <div key={color} style={styles.colorOption}>
                        <input
                          type="radio"
                          style={styles.colorRadio}
                          name="tempCoef"
                          value={color}
                          checked={tempCoef === color}
                          onChange={(e) => setTempCoef(e.target.value)}
                          id={`tempCoef-${color}`}
                        />
                        <label
                          htmlFor={`tempCoef-${color}`}
                          style={{
                            ...styles.colorLabel,
                            background: colorHex[color],
                            color: ['brown', 'red', 'blue', 'violet'].includes(color) ? '#fff' : '#000',
                            ...(tempCoef === color ? styles.colorLabelChecked : {})
                          }}
                        >
                          {t(`color_${color}`)}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={styles.resistorVisual}>
                <div style={styles.resistorBody}>
                  <div style={styles.resistorLead}></div>
                  <div style={{...styles.resistorBand, background: colorHex[band1]}}></div>
                  <div style={{...styles.resistorBand, background: colorHex[band2]}}></div>
                  {bandCount >= 5 && <div style={{...styles.resistorBand, background: colorHex[band3]}}></div>}
                  <div style={{...styles.resistorBand, background: colorHex[multiplier]}}></div>
                  <div style={styles.resistorSpacer}></div>
                  <div style={{...styles.resistorBand, background: colorHex[tolerance]}}></div>
                  {bandCount === 6 && <div style={{...styles.resistorBand, background: colorHex[tempCoef]}}></div>}
                  <div style={styles.resistorLead}></div>
                </div>
              </div>

              <button type="submit" style={styles.calculateBtn}>{t("decodeButton")}</button>
            </form>

            {colorCodeResults && (
              <div style={styles.results}>
                <h3 style={styles.resultsTitle}>{t("resultsTitle")}</h3>
                <div style={styles.resultGrid}>
                  <div style={{...styles.resultItem, ...styles.resultHighlight}}>
                    <span style={styles.resultLabel}>{t("resistanceLabel")}</span>
                    <span style={styles.resultValueHighlight}>{colorCodeResults.resistance}</span>
                  </div>
                  <div style={styles.resultItem}>
                    <span style={styles.resultLabel}>{t("toleranceResultLabel")}</span>
                    <span style={styles.resultValue}>{colorCodeResults.tolerance}</span>
                  </div>
                  {colorCodeResults.tempCoef && (
                    <div style={styles.resultItem}>
                      <span style={styles.resultLabel}>{t("tempCoefResultLabel")}</span>
                      <span style={styles.resultValue}>{colorCodeResults.tempCoef}</span>
                    </div>
                  )}
                  <div style={styles.resultItem}>
                    <span style={styles.resultLabel}>{t("minValueLabel")}</span>
                    <span style={styles.resultValue}>{colorCodeResults.minValue}</span>
                  </div>
                  <div style={styles.resultItem}>
                    <span style={styles.resultLabel}>{t("maxValueLabel")}</span>
                    <span style={styles.resultValue}>{colorCodeResults.maxValue}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'series' && (
          <div>
            <form style={styles.form} onSubmit={handleSeriesCalculate}>
              <div style={styles.formGroup}>
                <h3 style={styles.h2}>{t("seriesTitle")}</h3>
                <p style={{...styles.paragraph, marginBottom: '1.5rem'}}>{t("seriesFormula")}</p>
                <div>
                  {seriesResistors.map((resistor, index) => (
                    <div key={index} style={styles.resistorInputGroup}>
                      <input
                        type="number"
                        step="any"
                        placeholder={`${t("resistorPlaceholder")} ${index + 1} (Ω)`}
                        value={resistor || ''}
                        onChange={(e) => {
                          const newResistors = [...seriesResistors];
                          newResistors[index] = parseFloat(e.target.value) || 0;
                          setSeriesResistors(newResistors);
                        }}
                        style={styles.input}
                        required
                      />
                      {seriesResistors.length > 2 && (
                        <button
                          type="button"
                          style={styles.removeBtn}
                          onClick={() => removeSeriesResistor(index)}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button type="button" style={styles.addBtn} onClick={addSeriesResistor}>
                  {t("addResistorButton")}
                </button>
              </div>
              <button type="submit" style={styles.calculateBtn}>{t("calculateSeriesButton")}</button>
            </form>

            {seriesResult !== null && (
              <div style={styles.results}>
                <h3 style={styles.resultsTitle}>{t("seriesResultsTitle")}</h3>
                <div style={styles.resultGrid}>
                  <div style={{...styles.resultItem, ...styles.resultHighlight}}>
                    <span style={styles.resultLabel}>{t("totalResistanceLabel")}</span>
                    <span style={styles.resultValueHighlight}>{formatResistance(seriesResult)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'parallel' && (
          <div>
            <form style={styles.form} onSubmit={handleParallelCalculate}>
              <div style={styles.formGroup}>
                <h3 style={styles.h2}>{t("parallelTitle")}</h3>
                <p style={{...styles.paragraph, marginBottom: '1.5rem'}}>{t("parallelFormula")}</p>
                <div>
                  {parallelResistors.map((resistor, index) => (
                    <div key={index} style={styles.resistorInputGroup}>
                      <input
                        type="number"
                        step="any"
                        placeholder={`${t("resistorPlaceholder")} ${index + 1} (Ω)`}
                        value={resistor || ''}
                        onChange={(e) => {
                          const newResistors = [...parallelResistors];
                          newResistors[index] = parseFloat(e.target.value) || 0;
                          setParallelResistors(newResistors);
                        }}
                        style={styles.input}
                        required
                      />
                      {parallelResistors.length > 2 && (
                        <button
                          type="button"
                          style={styles.removeBtn}
                          onClick={() => removeParallelResistor(index)}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <button type="button" style={styles.addBtn} onClick={addParallelResistor}>
                  {t("addResistorButton")}
                </button>
              </div>
              <button type="submit" style={styles.calculateBtn}>{t("calculateParallelButton")}</button>
            </form>

            {parallelResult !== null && (
              <div style={styles.results}>
                <h3 style={styles.resultsTitle}>{t("parallelResultsTitle")}</h3>
                <div style={styles.resultGrid}>
                  <div style={{...styles.resultItem, ...styles.resultHighlight}}>
                    <span style={styles.resultLabel}>{t("totalResistanceLabel")}</span>
                    <span style={styles.resultValueHighlight}>{formatResistance(parallelResult)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("introTitle")}</h2>
        <p style={styles.paragraph}>{t("introParagraph1")}</p>
        <p style={styles.paragraph}>{t("introParagraph2")}</p>
        <p style={styles.paragraph}>{t("introParagraph3")}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("howToUseTitle")}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}>{t("howToStep1")}</li>
          <li style={styles.listItem}>{t("howToStep2")}</li>
          <li style={styles.listItem}>{t("howToStep3")}</li>
          <li style={styles.listItem}>{t("howToStep4")}</li>
          <li style={styles.listItem}>{t("howToStep5")}</li>
        </ol>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("insightsTitle")}</h2>
        <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem'}}>{t("insightsSubtitle1")}</h3>
        <p style={styles.paragraph}>{t("insightsParagraph1")}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t("insightsPoint1")}</li>
          <li style={styles.listItem}>{t("insightsPoint2")}</li>
          <li style={styles.listItem}>{t("insightsPoint3")}</li>
        </ul>

        <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem', marginTop: '2rem'}}>{t("insightsSubtitle2")}</h3>
        <p style={styles.paragraph}>{t("insightsParagraph2")}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t("insightsPoint4")}</li>
          <li style={styles.listItem}>{t("insightsPoint5")}</li>
          <li style={styles.listItem}>{t("insightsPoint6")}</li>
        </ul>

        <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem', marginTop: '2rem'}}>{t("insightsSubtitle3")}</h3>
        <p style={styles.paragraph}>{t("insightsParagraph3")}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t("insightsPoint7")}</li>
          <li style={styles.listItem}>{t("insightsPoint8")}</li>
          <li style={styles.listItem}>{t("insightsPoint9")}</li>
        </ul>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("detailsTitle")}</h2>
        <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem'}}>{t("detailsSubtitle1")}</h3>
        <p style={styles.paragraph}>{t("detailsParagraph1")}</p>
        <p style={styles.paragraph}>{t("detailsParagraph2")}</p>

        <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem', marginTop: '2rem'}}>{t("detailsSubtitle2")}</h3>
        <p style={styles.paragraph}>{t("detailsParagraph3")}</p>
        <p style={styles.paragraph}>{t("detailsParagraph4")}</p>

        <h3 style={{...styles.h2, fontSize: screenSize === 'mobile' ? '1.25rem' : screenSize === 'tablet' ? '1.5rem' : '1.75rem', marginTop: '2rem'}}>{t("detailsSubtitle3")}</h3>
        <p style={styles.paragraph}>{t("detailsParagraph5")}</p>
        <p style={styles.paragraph}>{t("detailsParagraph6")}</p>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("faqTitle")}</h2>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faqQuestion1")}</h3>
          <p style={styles.faqAnswer}>{t("faqAnswer1")}</p>
        </div>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faqQuestion2")}</h3>
          <p style={styles.faqAnswer}>{t("faqAnswer2")}</p>
        </div>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faqQuestion3")}</h3>
          <p style={styles.faqAnswer}>{t("faqAnswer3")}</p>
        </div>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faqQuestion4")}</h3>
          <p style={styles.faqAnswer}>{t("faqAnswer4")}</p>
        </div>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t("faqQuestion5")}</h3>
          <p style={styles.faqAnswer}>{t("faqAnswer5")}</p>
        </div>
      </section>

      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t("referencesTitle")}</h2>
        <ul style={styles.list}>
          <li style={styles.listItem}>
            <a href="https://www.iec.ch/standards" style={styles.refLink} target="_blank" rel="noopener noreferrer">
              {t("reference1")}
            </a>
          </li>
          <li style={styles.listItem}>
            <a href="https://www.electronics-tutorials.ws/resistor/res_2.html" style={styles.refLink} target="_blank" rel="noopener noreferrer">
              {t("reference2")}
            </a>
          </li>
          <li style={styles.listItem}>
            <a href="https://www.allaboutcircuits.com/textbook/reference/chpt-2/resistor-color-codes/" style={styles.refLink} target="_blank" rel="noopener noreferrer">
              {t("reference3")}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}